import Avatar, { Badge } from 'components/Avatar'
import Icon from 'components/Icon'
import Spinner from 'components/Spinner'
import * as React from 'react'
import { observer } from 'mobx-react'
import Button from 'components/Button'
import QuestionIcon from 'jobs/components/QuestionIcon'
import { extractMessageFromError } from 'utils/error-util'
import { PromptMessageContentViewModel } from './PromptMessageContentViewModel'
import styles from './PromptMessageContent.sass'
import {
  Card,
  CardContent,
  CardFooter,
  CardFooterCopy,
  CardFooterItem,
  CardFooterSpacer,
} from '../MessageCard'

interface PromptMessageContentProps {
  vm: PromptMessageContentViewModel
  'data-test'?: string
}

/**
 * Prompt Message Content
 */
export default observer(function PromptMessageContent({
  vm,
  'data-test': dataTest,
}: PromptMessageContentProps) {
  const prompt = vm.prompt
  const onPromptView = vm.view

  return (
    <div className={styles.root} data-test={dataTest}>
      {prompt.deleted ? (
        <div className={styles.deletedText}>This prompt has been deleted.</div>
      ) : (
        <>
          <div className={styles.preamble}>Prompt</div>
          <Card>
            {vm.fetchPrompt.match({
              pending: () => <PromptLoading />,
              rejected: (err) => <PromptLoadingFailed error={err} />,
              resolved: () => (
                <>
                  <CardContent
                    title={prompt.title}
                    visual={
                      <QuestionIcon size={30} type={prompt.question?.type} />
                    }
                  >
                    {prompt.description}
                  </CardContent>
                  <CardFooter>
                    {!prompt.isPendingResponse && prompt.answeredBy && (
                      <>
                        <CardFooterItem>
                          <Avatar
                            size={36}
                            badge={Badge.CheckMark}
                            subject={prompt.answeredBy}
                          />
                        </CardFooterItem>
                        <CardFooterItem>
                          <CardFooterCopy title={prompt.answeredBy.displayName}>
                            {vm.completionTimestampText}
                          </CardFooterCopy>
                        </CardFooterItem>
                      </>
                    )}
                    <CardFooterSpacer />
                    <CardFooterItem>
                      {prompt.canAnswer && prompt.isPendingResponse ? (
                        <Button type="primary" onClick={onPromptView}>
                          Respond
                        </Button>
                      ) : (
                        <Button type="translucent" onClick={onPromptView}>
                          View
                        </Button>
                      )}
                    </CardFooterItem>
                  </CardFooter>
                </>
              ),
            })}
          </Card>
        </>
      )}
    </div>
  )
})

interface PromptLoadingProps {}

/**
 * Prompt Loading
 */
export function PromptLoading({}: PromptLoadingProps) {
  return (
    <div className={styles.promptLoading}>
      <Spinner />
    </div>
  )
}

interface PromptLoadingFailedProps {
  error?: unknown
}

/**
 * Prompt Loading Failed
 */
export function PromptLoadingFailed({ error }: PromptLoadingFailedProps) {
  return (
    <div className={styles.promptLoadingFailed}>
      <Icon name="info" rotate={180} />
      &nbsp; Loading prompt failed: {extractMessageFromError(error)}
    </div>
  )
}
